import type { ReactElement } from "react";

import dayjs from "dayjs";
import type { Point } from "highcharts";
import { renderToString } from "react-dom/server";

import {
    fullMonthAndYear,
    fullMonthDayAndYear,
    shortMonthDayAndYear,
    shortMonthDayAndYearWithDayOfWeek,
} from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { TODAY } from "app/pages/digital-marketing/backlinks/shared/BacklinksConsts";
import type { IHighChartTooltip } from "app/pages/digital-marketing/backlinks/shared/BacklinksTypes";
import { numFilter } from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

export const getTooltipHeaderOverTime = (timeGranularity: GranularityNames, points: Array<Partial<Point>>) => {
    let text: string;

    const dateMoment = dayjs.utc(points[0].x);

    if (timeGranularity === GranularityNames.MONTHLY) {
        text = dateMoment.format(fullMonthDayAndYear);
    } else if (timeGranularity === GranularityNames.WEEKLY) {
        text = dateMoment.format(shortMonthDayAndYear);
    } else {
        text = dateMoment.format(shortMonthDayAndYearWithDayOfWeek[0]);
    }

    return <span>{text}</span>;
};

export const getTooltipHeaderNewVsLost = (timeGranularity: GranularityNames, points: Array<Partial<Point>>) => {
    let text: string;

    const dateMoment = dayjs.utc(points[0].x);

    if (timeGranularity === GranularityNames.MONTHLY) {
        text = dateMoment.format(fullMonthAndYear);
    } else if (timeGranularity === GranularityNames.WEEKLY) {
        const endOfWeek = dateMoment.endOf("isoWeek");
        text = `${dateMoment.format(shortMonthDayAndYear)} - ${(endOfWeek.isAfter(TODAY) ? TODAY : endOfWeek).format(shortMonthDayAndYear)}`;
    } else {
        text = dateMoment.format(shortMonthDayAndYearWithDayOfWeek[0]);
    }

    return <span>{text}</span>;
};

export const BacklinksChartYAxisParams = {
    gridLineWidth: 0.5,
    showFirstLabel: true,
    showLastLabel: true,
    reversed: false,
    gridZIndex: 2,
    reversedStacks: true,
    tickPixelInterval: 50,
    title: { enabled: false },
    labels: {
        style: {
            textTransform: "uppercase",
            fontSize: "11px",
            fontFamily: "Roboto",
            color: colorsPalettes.carbon[300],
        },
        formatter: ({ value }: { value: number }) => numFilter(value),
    },
};

export const getTooltipFormatter = (
    timeGranularity: GranularityNames,
    getChartTooltip: (props: IHighChartTooltip) => ReactElement,
) => {
    const meta = {
        isSingleMode: false,
    };

    return function (this: Point) {
        const ChartTooltip = getChartTooltip({
            points: this.points,
            x: this.x,
            timeGranularity: timeGranularity,
            meta,
        });
        return renderToString(ChartTooltip);
    };
};
